import {Observable, throwError as observableThrowError} from 'rxjs';
/**
 * Created by ptdave on 4/28/17.
 */
import {Injectable} from '@angular/core';
import {Location} from '@angular/common';


import {Router} from '@angular/router';
import {ToastService} from './toast.service';
import {ApiClient} from './apiClient';
import {
  AchievementDto,
  AppropriateInstructionDto,
  AttendanceByPeriodDto, AttendanceOverviewDto, BehaviorInterventionDto, ContactLogDto, EvidenceDto,
  InitialContactDto, InterventionLogDto, IRIPDto, MeetingDto,
  ObservationDto, OtherFactorDto,
  StudentAssessmentDto, StudentBehaviorDto, StudentDto
} from '@sidr/dto-types';

@Injectable()
export class StudentService {
  static handleError(error: Response) {
    console.error(error.url);
    console.error(error.statusText);
    console.error(error.type);
    console.error(error);

    return observableThrowError(error || 'Server error');
  }

  search(value: string): Observable<StudentDto[]> {
    return this.http.get<StudentDto[]>(`/api/students/search/${value}`);
  }

  photos(studentIds: string[]): Observable<Record<string, string>> {
    return this.http.post<Record<string, string>>(`/api/students/photos`, studentIds);
  }

  getStudentPhoto(districtId: string, studentId: string): Observable<string> {
    return new Observable<string>((observer) => {
      this.http.get<Blob>(`/api/students/${districtId}/${studentId}/photo`, {responseType: 'blob'})
      .subscribe((data: Blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          observer.next(reader.result as string);
          observer.complete();
        };
        if (data) {
          reader.readAsDataURL(data);
        }
      }, err => {
        observer.error(err);
      });
    });
  }

  goto(student: StudentDto) {
    this.router.navigate(['/student', student.id]);
  }

  get(districtId: string, studentId: string): Observable<StudentDto> {
    return this.http.get<StudentDto>(`/api/students/${districtId}/${studentId}`);
  }

  getAchievements(districtId: string, studentId: string): Observable<AchievementDto[]> {
    return this.http.get<AchievementDto[]>(`/api/students/${districtId}/${studentId}/achievements`);
  }

  postAchievement(districtId: string, studentId: string, achievement: AchievementDto): Observable<AchievementDto> {
    return this.http.post<AchievementDto>(`/api/students/${districtId}/${studentId}/achievements`, achievement);
  }

  patchAchievement(districtId: string, studentId: string, achievement: AchievementDto): Observable<AchievementDto> {
    return this.http.patch<AchievementDto>(`/api/students/${districtId}/${studentId}/achievements/${achievement.id}`, achievement);
  }

  deleteAchievement(districtId: string, studentId: string, achievementId: string): Observable<Response> {
    return this.http.delete<any>(`/api/students/${districtId}/${studentId}/achievements/${achievementId}`);
  }

  getAppropriateInstruction(districtId: string, studentId: string): Observable<AppropriateInstructionDto> {
    return this.http.get<AppropriateInstructionDto>(`/api/students/${districtId}/${studentId}/appropriate_instruction`);
  }

  patchAppropriateInstruction(districtId: string, studentId: string, model: AppropriateInstructionDto) {
    return this.http.patch<AppropriateInstructionDto>(`/api/students/${districtId}/${studentId}/appropriate_instruction`, model);
  }

  getAppropriateInstructionLegacy(districtId: string, studentId: string): Observable<AppropriateInstructionDto> {
    return this.http.get<AppropriateInstructionDto>(`/api/students/${districtId}/${studentId}/appropriate_instruction/v1`);
  }

  getAssessments(districtId: string, studentId: string): Observable<StudentAssessmentDto[]> {
    return this.http.get<StudentAssessmentDto[]>(`/api/students/${districtId}/${studentId}/assessments`);
  }

  getAttendance(districtId: string, studentId: string): Observable<AttendanceOverviewDto[]> {
    return this.http.get<AttendanceOverviewDto[]>(`/api/students/${districtId}/${studentId}/attendance_overview`);
  }

  getAttendanceByPeriod(districtId: string, studentId: string): Observable<AttendanceByPeriodDto[]> {
    return this.http.get<AttendanceByPeriodDto[]>(`/api/students/${districtId}/${studentId}/attendance_by_period`);
  }

  getBehaviors(districtId: string, studentId: string): Observable<StudentBehaviorDto[]> {
    return this.http.get<StudentBehaviorDto[]>(`/api/students/${districtId}/${studentId}/behaviors`);
  }

  getContacts(districtId: string, studentId: string): Observable<ContactLogDto[]> {
    return this.http.get<ContactLogDto[]>(`/api/students/${districtId}/${studentId}/contacts`);
  }

  getInitialContact(districtId: string, studentId: string): Observable<InitialContactDto> {
    return this.http.get<InitialContactDto>(`/api/students/${districtId}/${studentId}/initial_contact`);
  }

  patchInitialContact(districtId: string, studentId: string, init: InitialContactDto): Observable<InitialContactDto> {
    return this.http.patch<InitialContactDto>(`/api/students/${districtId}/${studentId}/initial_contact`, init);
  }

  postContact(districtId: string, studentId: string, contact: ContactLogDto): Observable<ContactLogDto> {
    return this.http.post<ContactLogDto>(`/api/students/${districtId}/${studentId}/contacts`, contact);
  }

  patchContact(districtId: string, studentId: string, contact: ContactLogDto): Observable<ContactLogDto> {
    return this.http.patch<ContactLogDto>(`/api/students/${districtId}/${studentId}/contacts/${contact.id}`, contact);
  }

  getOtherFactors(districtId: string, studentId: string): Observable<OtherFactorDto[]> {
    return this.http.get<OtherFactorDto[]>(`/api/students/${districtId}/${studentId}/other_factors`);
  }

  postOtherFactors(districtId: string, studentId: string, other_factor: OtherFactorDto): Observable<OtherFactorDto> {
    return this.http.post<OtherFactorDto>(`/api/students/${districtId}/${studentId}/other_factors`, other_factor);
  }

  deleteOtherFactors(districtId: string, studentId: string, other_factorId: string): Observable<Response> {
    return this.http.delete<any>(`/api/students/${districtId}/${studentId}/other_factors/${other_factorId}`);
  }

  patchOtherFactors(districtId: string, studentId: string, other_factor: OtherFactorDto): Observable<OtherFactorDto> {
    return this.http.patch<OtherFactorDto>(`/api/students/${districtId}/${studentId}/other_factors/${other_factor.id}`, other_factor);
  }

  getEvidence(districtId: string, studentId: string): Observable<EvidenceDto[]> {
    return this.http.get<EvidenceDto[]>(`/api/students/${districtId}/${studentId}/evidence`);
  }

  postEvidence(districtId: string, studentId: string, evidence: EvidenceDto): Observable<EvidenceDto> {
    return this.http.post<EvidenceDto>(`/api/students/${districtId}/${studentId}/evidence`, evidence);
  }

  patchEvidence(districtId: string, studentId: string, evidence: EvidenceDto): Observable<EvidenceDto> {
    return this.http.patch<EvidenceDto>(`/api/students/${districtId}/${studentId}/evidence/${evidence.id}`, evidence);
  }

  deleteEvidence(districtId: string, studentId: string, evidenceId: string): Observable<Response> {
    return this.http.delete<any>(`/api/students/${districtId}/${studentId}/evidence/${evidenceId}`);
  }

  getApprInst(districtId: string, studentId: string): Observable<AppropriateInstructionDto> {
    return this.http.get<AppropriateInstructionDto>(`/api/students/${districtId}/${studentId}/appropriate_instruction`);
  }

  patchApprInst(districtId: string, studentId: string, apprInst: AppropriateInstructionDto) {
    return this.http.patch<AppropriateInstructionDto>(`/api/students/${districtId}/${studentId}/appropriate_instruction`, apprInst);
  }

  getBehaviorIntervention(districtId: string, studentId: string): Observable<BehaviorInterventionDto[]> {
    return this.http.get<BehaviorInterventionDto[]>(`/api/students/${districtId}/${studentId}/behavior_interventions`);
  }

  patchBehaviorIntervention(districtId: string, studentId: string, bhvrintv: BehaviorInterventionDto): Observable<BehaviorInterventionDto> {
    return this.http.patch<BehaviorInterventionDto>(
      `/api/students/${districtId}/${studentId}/behavior_interventions/${bhvrintv.id}`, bhvrintv);
  }

  postBehaviorIntervention(districtId: string, studentId: string, bhvrintv: BehaviorInterventionDto): Observable<BehaviorInterventionDto> {
    return this.http.post<BehaviorInterventionDto>(`/api/students/${districtId}/${studentId}/behavior_interventions`, bhvrintv);
  }

  deleteBehaviorIntervention(districtId: string, studentId: string, bhvrintv: string): Observable<Response> {
    return this.http.delete<any>(`/api/students/${districtId}/${studentId}/behavior_interventions/${bhvrintv}`);
  }

  getInterventionLogs(districtId: string, studentId: string): Observable<InterventionLogDto[]> {
    return this.http.get<InterventionLogDto[]>(`/api/students/${districtId}/${studentId}/interventions`);
  }

  getInterventionLog(districtId: string, studentId: string, id: string): Observable<InterventionLogDto> {
    return this.http.get<InterventionLogDto>(`/api/students/${districtId}/${studentId}/intervention/${id}`);
  }

  postInterventionLog(districtId: string, studentId: string, intv: InterventionLogDto): Observable<InterventionLogDto> {
    return this.http.post<InterventionLogDto>(`/api/students/${districtId}/${studentId}/intervention`, intv);
  }

  patchInterventionLog(districtId: string, studentId: string, intv: InterventionLogDto): Observable<InterventionLogDto> {
    return this.http.patch<InterventionLogDto>(`/api/students/${districtId}/${studentId}/intervention/${intv.id}`, intv);
  }

  deleteInterventionLog(districtId: string, studentId: string, intvId: string): Observable<Response> {
    return this.http.delete<any>(`/api/students/${districtId}/${studentId}/intervention/${intvId}`);
  }

  postIRIP(districtId: string, studentId: string, data: IRIPDto): Observable<IRIPDto> {
    return this.http.post<IRIPDto>(`/api/students/${districtId}/${studentId}/irips`, data);
  }

  patchIRIP(districtId: string, studentId: string, data: IRIPDto): Observable<IRIPDto> {
    return this.http.patch<IRIPDto>(`/api/students/${districtId}/${studentId}/irips/${data.id}`, data);
  }

  deleteIRIP(districtId: string, studentId: string, id: string): Observable<any> {
    return this.http.delete(`/api/students/${districtId}/${studentId}/irips/${id}`);
  }

  getIRIPs(districtId: string, studentId: string): Observable<IRIPDto[]> {
    return this.http.get<IRIPDto[]>(`/api/students/${districtId}/${studentId}/irips`);
  }

  getIRIPPrint(districtId: string, studentId: string, id: string): Observable<any> {
    return this.http.get<Blob>(`/api/students/${districtId}/${studentId}/irips/${id}/print`, {
      responseType: 'blob',
    });
  }

  getObservations(districtId: string, studentId: string): Observable<ObservationDto[]> {
    return this.http.get<ObservationDto[]>(`/api/students/${districtId}/${studentId}/observations`);
  }

  postObservation(districtId: string, studentId: string, obs: ObservationDto): Observable<ObservationDto> {
    return this.http.post<ObservationDto>(`/api/students/${districtId}/${studentId}/observation`, obs);
  }

  patchObservation(districtId: string, studentId: string, obs: ObservationDto): Observable<ObservationDto> {
    return this.http.patch<ObservationDto>(`/api/students/${districtId}/${studentId}/observation/${obs.id}`, obs);
  }

  deleteObservation(districtId: string, studentId: string, obs: ObservationDto): Observable<ObservationDto> {
    return this.http.delete<any>(`/api/students/${districtId}/${studentId}/observation/${obs.id}`);
  }

  getMeetings(districtId: string, studentId: string): Observable<MeetingDto[]> {
    return this.http.get<MeetingDto[]>(`/api/students/${districtId}/${studentId}/meetings`);
  }

  postMeeting(districtId: string, studentId: string, m: MeetingDto): Observable<MeetingDto> {
    return this.http.post<MeetingDto>(`/api/students/${districtId}/${studentId}/meeting`, m);
  }

  patchMeeting(districtId: string, studentId: string, m: MeetingDto): Observable<MeetingDto> {
    return this.http.patch<MeetingDto>(`/api/students/${districtId}/${studentId}/meeting/${m.id}`, m);
  }

  deleteMeeting(districtId: string, studentId: string, meetingId: string): Observable<Response> {
    return this.http.delete<any>(`/api/students/${districtId}/${studentId}/meeting/${meetingId}`);
  }

  constructor(
    private http: ApiClient,
    private router: Router,
  ) {
  }
}
